import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Fuel, BatteryCharging } from "lucide-react";
import { formatNGN } from "@/lib/api";
import { BRAND_ASSETS } from "@/lib/brand";
import PaymentBadges from "@/components/PaymentBadges";

const VALUE_POINTS = [
  { icon: Zap, text: "Saves up to 75% on electricity and prepaid units on standard grid power." },
  { icon: Fuel, text: "Runs effortlessly on small generators without tripping or overloading them." },
  { icon: BatteryCharging, text: "Solar & Inverter safe: iron anytime without draining your battery storage." },
];

// Defers the 2.7MB hero video until the page has finished loading so it never competes with first paint.
const DeferredHeroVideo = () => {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let timer;
    const arm = () => { timer = setTimeout(() => setSrc(BRAND_ASSETS.heroVideo), 800); };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return () => { clearTimeout(timer); window.removeEventListener("load", arm); };
  }, []);

  useEffect(() => {
    if (src && ref.current) ref.current.play().catch(() => {});
  }, [src]);

  return (
    <video
      ref={ref}
      data-testid="hero-video"
      src={src || undefined}
      poster={BRAND_ASSETS.heroVideoPoster || BRAND_ASSETS.heroImage || undefined}
      muted
      loop
      playsInline
      preload="none"
      controls
      className="w-full h-full object-cover"
    />
  );
};

const HeroVisual = () =>
  BRAND_ASSETS.heroVideo ? (
    <div className="relative overflow-hidden glossy-deep rounded-2xl" style={{ aspectRatio: "16/9" }} data-testid="hero-video-wrap">
      <DeferredHeroVideo />
    </div>
  ) : (
    <div className="relative overflow-hidden glossy-deep rounded-2xl" style={{ aspectRatio: "4/5" }} data-testid="hero-product-image">
      <img
        src={BRAND_ASSETS.heroImage}
        alt="A tailor holding a pressed agbada beside the SunPress CORE 500 low-watt dry iron and its retail box"
        className="w-full h-full object-cover"
        fetchPriority="high"
      />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6" style={{ background: "linear-gradient(180deg, transparent, rgba(10,10,10,0.72))" }}>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="font-serif text-2xl sm:text-3xl" style={{ color: "#FCF6EA", lineHeight: 1 }}>
              CORE <span className="sunset-text">500</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono mt-2" style={{ color: "rgba(252,246,234,0.75)" }}>
              Low-watt dry iron
            </div>
          </div>
          <div className="text-right text-[10px] uppercase tracking-widest font-mono" style={{ color: "rgba(252,246,234,0.75)" }}>
            500W
            <br />
            Solar optimised
          </div>
        </div>
      </div>
    </div>
  );

export default function HeroSection({ core }) {
  return (
    <section className="theme-deep hero-deep relative overflow-hidden" data-testid="hero-section">
      <div className="sunset-ambient" style={{ top: "-180px", right: "-180px" }} />
      <div className="sunset-ambient" style={{ bottom: "-220px", left: "-220px", animationDelay: "4s" }} />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-14 md:pt-28 pb-16 md:pb-32 grid md:grid-cols-12 gap-10 items-center relative z-10">
        <div className="md:col-span-7 relative z-10">
          <div className="eyebrow fade-up">The SunPress Mission</div>
          <h1 className="display fade-up-2 mt-6" style={{ fontSize: "clamp(2.4rem, 6vw, 5.4rem)" }}>
            We make appliances that <em className="italic" style={{ color: "var(--brand)" }}>respect</em> your power.
          </h1>
          <p className="fade-up-3 mt-6 font-serif text-xl sm:text-2xl md:text-3xl leading-snug" style={{ color: "var(--ink)" }} data-testid="hero-subheadline">
            An ordinary iron <em className="italic">fights</em> your power supply. <span className="sunset-text">This one doesn't.</span>
          </p>
          <p className="fade-up-3 mt-6 max-w-xl text-base sm:text-lg" style={{ color: "var(--ink-soft)" }} data-testid="hero-paragraph">
            A standard dry iron pulls 1,500W to 2,400W—spiking your electricity meter, tripping small
            generators, and instantly draining inverter batteries. The CORE 500 does the exact same
            heavy-duty pressing on just 500W. Whether you run on the national grid, prepaid meter units,
            a generator, or a solar/inverter setup, it delivers crisp, professional pressing without the
            power panic.
          </p>
          <ul className="fade-up-3 mt-8 space-y-3 max-w-xl" data-testid="hero-value-checklist">
            {VALUE_POINTS.map((v) => {
              const I = v.icon;
              return (
                <li key={v.text} className="flex items-start gap-3 text-sm sm:text-base" style={{ color: "var(--ink)" }}>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0 mt-0.5" style={{ background: "var(--brand-tint)" }}>
                    <I size={14} strokeWidth={2.4} style={{ color: "var(--sun-orange)" }} />
                  </span>
                  <span>{v.text}</span>
                </li>
              );
            })}
          </ul>
          <div className="fade-up-4 mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/products/CORE-500" data-testid="hero-shop-cta" className="btn-brand justify-center">
              {core ? <>Pre-Order for November Delivery — {formatNGN(core.price_ngn)}</> : <>Pre-Order for November Delivery</>} <ArrowRight size={16} />
            </Link>
            <a href="#see-it-in-action" data-testid="hero-learn-cta" className="btn-outline justify-center">
              See the difference
            </a>
          </div>
          <PaymentBadges className="fade-up-4 mt-6" />
        </div>

        <div className="md:col-span-5 relative">
          <HeroVisual />
          <div className="absolute -bottom-8 -left-8 hidden md:block px-5 py-4 lifted rounded-xl" style={{ zIndex: 20 }}>
            <div className="text-xs uppercase tracking-widest font-mono" style={{ color: "var(--ink-soft)" }}>CORE 500</div>
            <div className="font-serif text-2xl mt-1 sunset-text">500W</div>
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>vs 1,500–2,400W ordinary</div>
          </div>
        </div>
      </div>
    </section>
  );
}
